/**
 * Shared header bar for `IssuePanel`-wrapped filter cards.
 *
 * Renders: title + helper text on the left, `Reset filters` button on the
 * right. Used by `DataViewFilterPanel` (Filter issues) and the catalogue
 * review page's `CatalogueFilterPanel` (Filter products) so both surfaces
 * look identical and font/spacing tweaks only need to land in one place.
 *
 * Bottom margin is controlled by `tightBottom` — pass `true` when the
 * panel has no body content below the header (so the panel collapses to
 * just a header strip), otherwise the default 12 px gap reads as the
 * separator between header and the first body section.
 */
import React from 'react'
import { Button, Stack, Typography } from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { poolwerxColors } from '../../../theme/colors'

interface FilterPanelHeaderProps {
  title: string
  helperText?: string
  onReset: () => void
  hasAnyActiveFilter: boolean
  /** True when no body content follows; drops the bottom margin. */
  tightBottom?: boolean
}

const FilterPanelHeader: React.FC<FilterPanelHeaderProps> = ({
  title,
  helperText,
  onReset,
  hasAnyActiveFilter,
  tightBottom,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={2}
    sx={{ mb: tightBottom ? 0 : 1.5 }}
  >
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={1.25}
      sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0.25 }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {helperText && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', lineHeight: 1.4 }}
        >
          {helperText}
        </Typography>
      )}
    </Stack>
    <Button
      size="small"
      variant="text"
      startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
      disabled={!hasAnyActiveFilter}
      onClick={onReset}
      sx={{
        textTransform: 'none',
        color: 'text.secondary',
        '&:not(:disabled)': { color: poolwerxColors.teal },
        flexShrink: 0,
      }}
    >
      Reset filters
    </Button>
  </Stack>
)

export default FilterPanelHeader
